
/// <amd-module name="Checks/TaskChecks/Components/ArtifactPolicyCheckComponent" />
import React = require("react");

import * as Resources from "Checks/TaskChecks/Resources";
import { CheckConfiguration, Resource, TaskCheckConfiguration } from "Checks/Client/Contracts/PipelinesChecks";
import { TaskCheckConfig } from "Checks/TaskCheck/Client/Contracts/TaskCheck";

import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentProperties, VssComponent, IVssComponentContext, IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";

import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";

export interface IArtifactPolicyCheckProps extends IVssComponentProperties {
    resource: Resource;
    initialCheckConfiguration: TaskCheckConfig | undefined;
    updateCheckDataAndEnableSave: (check: CheckConfiguration) => void;
    disableCreateButton: (value: boolean) => void;
    updateTelemetryData: (telemetryData: { [key: string]: string }) => void;
}

export class ArtifactPolicyCheckComponent extends VssComponent<IArtifactPolicyCheckProps>{
    public constructor(props: IArtifactPolicyCheckProps, context: IVssComponentContext) {
        super(props, context);

        this._displayName = new ObservableValue(props.initialCheckConfiguration ? props.initialCheckConfiguration.displayName : "");
        this._policyContent = new ObservableValue(props.initialCheckConfiguration ? props.initialCheckConfiguration.inputs[policyContentKeyName] : "");
    }

    public render(): JSX.Element {
        return (
            <Observer displayName={this._displayName} policyContent={this._policyContent}>
                {(observedProps: { displayName: string, policyContent: string }) => {
                    const policyLengthExceededError = isPolicyLinesLimitExceeded(observedProps
                        .policyContent);
                    const compulsoryFieldsAbsent = !compulsoryFieldsValidationRegex.test(observedProps.policyContent);
                    return (
                        <div className="artifact-policy-check-panel">
                            <div className={"flex-column artifact-policy-check-panel-field-group"}>
                                <FormItem
                                    label={Resources.DisplayName}
                                    className={"artifact-policy-display-name-field body-m margin-top-16"}
                                >
                                    <TextField
                                        value={observedProps.displayName}
                                        onChange={this._onDisplayNameChange}
                                    />
                                </FormItem>


                                <FormItem
                                    label={this._getPolicyContentLabel()}
                                    className={"artifact-policy-policy-content-field body-m margin-top-16"}
                                    message={(compulsoryFieldsAbsent && !!observedProps.policyContent)
                                        ? Resources.CompulsoryFieldsMissing
                                        : (
                                            policyLengthExceededError
                                                ? Resources.LinesExceededErrorMessage
                                                : (<FormatComponent format={Resources.RegoContentSubTextFormat}>
                                                    <Link
                                                        rel="noreferrer noopener nofollow"
                                                        className={"see-eg-link"}
                                                        ariaLabel={Resources.SeeExamplesDetailed}
                                                        href={regoGuidanceLink}>
                                                        {Resources.SeeExamples}
                                                    </Link>
                                                </FormatComponent>))}
                                    error={policyLengthExceededError || (compulsoryFieldsAbsent && !!observedProps.policyContent)}
                                >
                                    <TextField
                                        value={observedProps.policyContent}
                                        onChange={this._onPolicyContentChange}
                                        multiline={true}
                                        ariaLabelledBy={policyContentLabelId}
                                        rows={5}
                                        autoAdjustHeight={true}
                                    />
                                </FormItem>
                            </div>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private _onDisplayNameChange = (event: any, value: string) => {
        this._displayName.value = value;
        this._updateCheckState();
    }

    private _onPolicyContentChange = (event: any, value: string) => {
        this._policyContent.value = value;
        this._updateCheckState();
    }

    private _updateCheckState() {
        if (!this._displayName.value || !this._policyContent.value || isPolicyLinesLimitExceeded(this._policyContent.value) || !compulsoryFieldsValidationRegex.test(this._policyContent.value)) {
            this.props.disableCreateButton(true);
        }
        else {
            this.props.updateCheckDataAndEnableSave(this.getCheckConfiguration());
            this.props.updateTelemetryData(this._getTelemetryData());
        }
    }

    private _getTelemetryData(): { [key: string]: string } {
        return {
            CheckType: taskCheckTypeName,
            CheckDefinitionId: artifactPolicyTaskId
        };
    }

    private _getPolicyContentLabel(): React.ReactNode {
        return (<div className={"flex flex-row"}>
            <div id={getSafeId(policyContentLabelId)} className={"flex-grow flex-self-center"}>{Resources.RegoContent}</div>
            <Button onClick={this._onBrowseTemplatesClick} ariaLabel={Resources.UseTemplates} >
                {Resources.UseTemplates}
            </Button>
        </div>);

    }

    private _onBrowseTemplatesClick = () => {
        this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager")
            .renderCallout((onDismiss) => <WrappedComponent
                wrappedType={"PolicyDefinitionTemplatesPanel"}
                dependencies={[
                    "ms.vss-pipelinechecks.artifact-policy-templates-content",
                    "ms.vss-pipelinechecks.artifact-policy-definition-template-contributions",
                    "ms.vss-pipelinechecks.artifact-policy-definition-templates"
                ]}
                onDismiss={onDismiss}
                updateTemplateContent={(policy: string) => this._onPolicyContentChange(null, policy)} />);
    }

    private getCheckConfiguration(): TaskCheckConfiguration {
        return {
            type: {
                id: taskCheckTypeId,
                name: taskCheckTypeName
            },
            settings: {
                definitionRef: {
                    id: artifactPolicyTaskId,
                    name: artifactPolicyTaskName,
                    version: "0.0"
                },
                displayName: this._displayName.value,
                inputs: { [policyContentKeyName]: this._policyContent.value }
            } as TaskCheckConfig,
            resource: this.props.resource
        } as TaskCheckConfiguration;
    }

    private _displayName: IObservableValue<string>;
    private _policyContent: IObservableValue<string>;
}

const taskCheckTypeId = "fe1de3ee-a436-41b4-bb20-f6eb4cb879a7";
const taskCheckTypeName = "Task Check";
const artifactPolicyTaskId = "1B4FDD02-B979-480D-9A7E-0B7AF7FE471F";
const artifactPolicyTaskName = "evaluateArtifactPolicies";
const policyContentKeyName: string = "PolicyContent";
const policyContentLinesLimit: number = 5000;

const regoGuidanceLink: string = "https://aka.ms/AA6e0a2";

const compulsoryFieldsValidationRegex = /^(?=[^]*package[ ]*[\w]+[\s]+)(?=[^]*violations[\s]*=[\s]*[\w\"]+[\s]*[\{]*[\w\"\s\}]*)[^]*$/im;
const policyContentLabelId = "artifact-policy-content-label";

function isPolicyLinesLimitExceeded(policyContent: string): boolean {
    return policyContent.split("\n").length > policyContentLinesLimit;
}
